/**
 * field.never_engaged — high-value members the workspace has never
 * touched. Surfaces the gap between intent (high relationship_score)
 * and action (zero touches). Click-through opens the member portal.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { Target } from "lucide-react";
import { WidgetProps } from "../registry";

export function FieldNeverEngaged({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 8;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "field.never_engaged", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/members-index`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Engagement coverage could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" className="space-y-1.5"><span className="sr-only">Loading…</span><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-3/4" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-1/2" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-2/3" /></div>;
  const members: any[] = data?.members ?? [];
  const untouched = members
    .filter((m) => (m.touch_count ?? 0) === 0 && (m.relationship_score ?? 0) > 0)
    .sort((a, b) => (b.relationship_score ?? 0) - (a.relationship_score ?? 0))
    .slice(0, limit);

  if (untouched.length === 0) {
    return <div className="flex items-center gap-1.5 text-stone-600 text-xs"><Target size={13} aria-hidden="true" className="text-emerald-700" />All high-value members have been engaged.</div>;
  }

  return (
    <ul className="space-y-1 text-xs">
      <li className="text-stone-600 text-[11px] pb-1 border-b border-stone-100">
        {untouched.length} unengaged · ranked by potential
      </li>
      {untouched.map((m) => {
        const partyChar = (m.party || "?").charAt(0).toUpperCase();
        const partyClass = partyChar === "R" ? "text-red-700" : partyChar === "D" ? "text-blue-800" : "text-amber-700";
        return (
          <li key={m.bioguide_id}>
            <Link
              to={`/members/${encodeURIComponent(m.bioguide_id)}`}
              className="flex items-center gap-2 hover:bg-stone-50 px-1 py-0.5 rounded -mx-1"
            >
              <span className={`font-bold ${partyClass}`}>{partyChar}</span>
              <span className="flex-1 truncate text-stone-800">{m.display_name}</span>
              <span className="font-mono text-stone-500 text-[10px]">{m.state}</span>
              <span className="font-mono text-stone-500 tabular-nums text-[11px]">{m.relationship_score}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
